import { reactive, computed } from "vue"
import { defineStore } from "pinia"
import axios from "axios"

import useNotify from "@Composables/useNotify.js"

import OrganizationModel from "@Modules/teams/Data/Models/OrganizationModel"
import type { OrganizationData } from "@Modules/teams/Data/Models/OrgTypes"
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel"
import { Team } from "@Modules/teams/Data/Models/TeamModel.ts"
import { isNullish } from "@tinyurldev/tu-ui-vue"

export type TeamState = {
  teamsEnabled: boolean
  organizations: Record<string, Organization>
  errors: Record<string, string>
}

export type UpdateOrgParams = {
  name: string
  description: string
  photo?: File | null
}

/**
 * The XTeamsStore pinia store is used to store information about teams, organizations, and workspaces.
 */
export const XTeamsStore = defineStore("x-teams", () => {
  const { notifySuccess } = useNotify();

  // Data
  const state = reactive<TeamState>({
    teamsEnabled: true,

    // A key/val list of organizations. The key is the slug of the org so that we can do efficient lookups
    organizations: {},

    // If there are errors fetching or submitting data to the server you can pass them back here
    errors: {},
  });

  //
  // Getters
  const errors = computed(() => state.errors)
  const organizations = computed(() => state.organizations);
  const isTeamsAllowed = computed(() => state.teamsEnabled);

  /**
   * Returns the organization with the provided slug or null if there is no match
   * @param String slug the slug of the organization we want
   */
  const getOrganizationBySlug = computed<(slug: string) => Organization | null>(
    () => {
      return (slug: string) => state?.organizations?.[slug] || null
    }
  )

  /**
   * Returns a list of workspaces for the organization matching the provided slug
   * @param String slug the slug of the organization we want
   */
  const getWorkspacesBySlug = computed(() => (slug: string) => {
    return state?.organizations?.[slug]?.teams || []
  })

  /**
   * Returns the workspace matching the provided slug and team abbreviation
   * @param String slug the slug of the organization we want
   * @param String abbv the abbreviation of the team we want
   */
  const getWorkspaceBySlugAndAbbv = computed(
    () => (slug: string, abbv: string) => {
      return state?.organizations?.[slug]?.teams?.find((team: Team) => {
        return team.abbreviation === abbv || null
      })
    }
  )

  /**
   * Returns the workspace matching the provided org slug and team slug
   * @param String slug the slug of the organization we want
   * @param String abbv the slug of the team we want
   */
  const getWorkspaceBySlugAndTeam = computed(
    () => (orgSlug: string, teamSlug: string) => {
      return state?.organizations?.[orgSlug]?.teams?.find((team: Team) => {
        return team.slug === teamSlug || null
      })
    }
  )

  //
  // Actions
  /**
   * The application has a god route called /state that is used to send down basically all information about the
   * application. This route contains a list of all organizations and workspaces that the current user is a
   * part of. Every time this state route is called, the orgs and teams will be passed to this store
   * using this method.
   * @param organizations a list of organizations and workspaces that this user has access to
   * @param teamsEnabled is teams enabled yes/no
   * @param shouldAddPersonalTeam if true we also add a "personal" team for this user
   */
  const initializeFromState = function (
    organizations: Array<OrganizationData> = [],
    teamsEnabled: boolean = true,
    shouldAddPersonalTeam: boolean = true
  ) {
    state.teamsEnabled = teamsEnabled

    // We want to store each organization under its so that we can easily locate it later so we
    // use the slug as a key
    const mappedOrgs: Record<string, Organization> = {}
    Object.values(organizations).map((org) => {
      mappedOrgs[org.slug] = OrganizationModel(org, { shouldAddPersonalTeam })
    })
    state.organizations = mappedOrgs
  }

  const updateOrganization = async function (
    organization: Organization,
    form: UpdateOrgParams
  ): Promise<void> {
    state.errors = {};
    const url = `/app/api/organization/${organization.slug}`

    const formData = new FormData();
    formData.append('_method', 'PUT');
    formData.append('name', form.name);
    formData.append('description', form.description);
    if (!isNullish(form.photo)) {
      formData.append('photo', form.photo);
    }

    const response = await axios
      .post(url, formData)
      .catch((e) => {
        const errors: Record<string, string> = {}
        Object.keys(e?.response?.data?.errors || {}).forEach((key) => {
          errors[key] = e.response.data.errors[key][0]
        })

        state.errors = errors
      })

    if (response?.status === 200) {
      const existingOrg = state.organizations[organization.slug]
      state.organizations = {
        ...state.organizations,
        [organization.slug]: {
          ...existingOrg,
          name: response.data.data.name,
          description: response.data.data.description,
          photo_url: response.data.data.photo_url,
        } as Organization,
      }

    notifySuccess("Organization Updated");
    }
  }

  return {
    organizations,
    errors,
    initializeFromState,
    getOrganizationBySlug,
    getWorkspacesBySlug,
    getWorkspaceBySlugAndAbbv,
    getWorkspaceBySlugAndTeam,
    isTeamsAllowed,
    updateOrganization,
    };
})
